import { formDataToStructuredObject } from "utils/formDataUtils";
import scraperApi, { ApiError, ApiErrorResponse } from "api";
import { addToasterActionData } from "hooks/useToasterFromActionData";
import { fmtCurrency } from "utils";
import { PricingOptionsDataRouteType } from "routes/data-routes/billing-data/pricing-options/route.meta";


function getPricingOptionErrorMessage(message: string | undefined): string {
  if (!message) {
    return "We couldn't save your billing preference. Please try again.";
  }

  if (/default payment method/i.test(message)) {
    return "Add a default payment method before enabling automatic Stripe billing.";
  }

  if (/billing address/i.test(message)) {
    return "Add a billing address before enabling automatic Stripe billing.";
  }

  if (/pay.?as.?you.?go|payg/i.test(message)) {
    return "Pay-as-you-go could not be enabled for this plan. Please review the spending cap and try again.";
  }

  if (/auto.?upgrade/i.test(message)) {
    return "Auto-upgrade could not be enabled for this plan. Please try again or choose another limit option.";
  }

  return message;
}


export const action: PricingOptionsDataRouteType["route"]["action"] = async ({ request }) => {
  const formData = formDataToStructuredObject(await request.formData());

  try {
    switch (formData["pricing-option-action"]) {
      case "auto-upgrade":
        const autoUpgradeSetting = formData["auto-upgrade"] ?? "true";
        const autoUpgradeResponse = await scraperApi.subscription.toggleAutoUpgrade(autoUpgradeSetting === "true");
        const isAutoUpgradeEnabled = autoUpgradeSetting === "true";

        return addToasterActionData(
          { pricingOptions: autoUpgradeResponse },
          {
            toastId: isAutoUpgradeEnabled ? "auto-upgrade-enabled" : "auto-upgrade-disabled",
            type: "success",
            title: "Changes Saved!",
            description: isAutoUpgradeEnabled
              ? "Auto-upgrade has been enabled for your account."
              : "Auto-upgrade has been disabled."
          }
        );

      case "pay-as-you-go":
        const payAsYouGoSetting = formData["pay-as-you-go"] ?? "true";
        const payAsYouGoCreditsCap = formData["pay-as-you-go-credits-cap"] ?? "-1";
        const payAsYouGoOption = formData["pay-as-you-go-option"];

        const payAsYouGoResponse = await scraperApi.subscription.togglePayAsYouGo(
          payAsYouGoSetting === "true",
          Number.parseInt(payAsYouGoCreditsCap, 10),
          payAsYouGoOption === "pay-as-you-go-and-contact-sales"
        );

        const isEnabling = payAsYouGoSetting === "true";
        const creditsCap = Number.parseInt(payAsYouGoCreditsCap, 10);
        const spendingCap = creditsCap > 0 ? (creditsCap * 0.60) / 1000 : 0;

        return addToasterActionData(
          { pricingOptions: payAsYouGoResponse },
          {
            toastId: isEnabling ? "payg-enabled" : "payg-disabled",
            type: "success",
            title: "Changes Saved!",
            description: isEnabling
              ? `Pay-as-you-go has been enabled with a cap of ${fmtCurrency(spendingCap)}.` // TODO pass currencyCode — not available in action handler scope
              : "Pay-as-you-go has been disabled."
          }
        );

      default:
        throw new Error("unsupported pricing option action");
    }

  } catch (error) {
    if (error instanceof ApiError) {
      const apiError = error as ApiError<ApiErrorResponse>;

      return {
        error: {
          message: getPricingOptionErrorMessage(apiError.message),
          meta: apiError.details
        }
      };
    }

    // TODO error handling
    return {
      error: {
        message: getPricingOptionErrorMessage((error as any).message),
      }
    };
  }
};
